import { Injectable, inject } from '@angular/core';
import { Router, type NavigationExtras, type UrlTree } from '@angular/router';
import { MtmContextService } from '@mtm/context-ui-angular';
import { DRIVE_CONTEXT_MINTER, type DriveContextMinter } from '@mtm/drive/components';
import { driveSelectionContextState, type DriveSelectionTarget } from './drive-context-state.utils';

/** Opens a Drive target in a project by minting the `?ctx=` address the explorer reads. */
@Injectable({ providedIn: 'root' })
export class DriveTargetNavigationService {
  private readonly router: Router = inject(Router);
  private readonly viewContext: MtmContextService = inject(MtmContextService);
  private readonly contextMinter: DriveContextMinter = inject(DRIVE_CONTEXT_MINTER);

  /** The `/projects/:id?ctx=` address that opens `target`. */
  public urlTree(projectId: string, target: DriveSelectionTarget): Promise<UrlTree> {
    return this.contextUrlTree(['/projects', projectId], target);
  }

  /** `route` with a minted `?ctx=` for `target` — also how a non-drive page receives a task to open. */
  public async contextUrlTree(
    route: readonly string[],
    target: DriveSelectionTarget,
    queryParams: Readonly<Record<string, string>> = {}
  ): Promise<UrlTree> {
    const ctx: string = await this.contextMinter.create(driveSelectionContextState(this.viewContext.state(), target));
    return this.router.createUrlTree([...route], { queryParams: { ...queryParams, ctx } });
  }

  public async open(projectId: string, target: DriveSelectionTarget, extras: NavigationExtras = {}): Promise<boolean> {
    return this.router.navigateByUrl(await this.urlTree(projectId, target), extras);
  }
}
